import {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Alert,
    Avatar,
    Box,
    Button,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Paper,
    Skeleton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";

import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import ContactMailOutlinedIcon from "@mui/icons-material/ContactMailOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import {
    alpha,
} from "@mui/material/styles";

import {
    PRIMARY_COLOR,
} from "../util/constants";

import {
    fetchUserAPI,
} from "../api/user_api";

import {
    fetchStripeCanReceiveAPI,
    fetchStripeReadyAPI,
    refreshStripeOnboardingAPI,
    startStripeOnboardingAPI,
} from "../api/payment_api";

import {
    handleResponse,
} from "../util/integration";

import {
    toEmptyStringIfNull,
} from "../util/utilities";

const ROLE = Object.freeze({
    admin: "admin",
    buyer: "buyer",
    seller: "seller",
    operator: "operator",
});

const LABEL = Object.freeze({
    active: "Aktiv",
    disabled: "Deaktiviert",

    administrator: "Administrator",
    buyer: "Käufer",
    seller: "Verkäufer",
    operator: "Operator",

    profile: "Profil",
    businessDetails: "Geschäftliche Angaben",
    sellerDocuments: "Verkäuferdokumente",

    person: "Person",
    contact: "Kontakt",
    address: "Adresse",
    tags: "Tags",

    company: "Firma",
    firstName: "Vorname",
    lastName: "Nachname",
    additionalDirectors: "Weitere Geschäftsführer",
    username: "Benutzername",
    emailAddress: "E-Mail-Adresse",
    mobilePhoneNumber: "Handynummer",

    bankAndTax: "Bank- und Steuerdaten",
    bank: "Bank",
    bic: "BIC",
    iban: "IBAN",
    taxId: "Steuernummer",
    vatId: "USt-ID",
    vat: "Umsatzsteuer",
    taxExempt: "Steuerbefreit",

    stripeAccountId: "Stripe-Konto-ID",

    invoiceDetails: "Rechnungsangaben",
    invoicePrefix: "Rechnungspräfix",
    transactionFees: "Transaktionsgebühr",

    registration: "Handelsregister",
    registrationCourt: "Registergericht",
    registrationNumber: "Registernummer",

    imprint: "Impressum",

    termsAndPrivacy: "AGB und Datenschutz",
    sellerTerms: "AGB",
    sellerPrivacyPolicy:
        "Datenschutzerklärung",
    pdfDocument: "PDF-Dokument",
    download: "Herunterladen",

    showSensitiveData:
        "Sensible Daten anzeigen",
    hideSensitiveData:
        "Sensible Daten ausblenden",
});

const MESSAGE = Object.freeze({
    emptyValue: "–",
    noAddress: "Keine Adresse hinterlegt.",
    noTags: "Keine Tags zugeordnet.",
    documentMissing: "Nicht hinterlegt",
    documentUnavailable:
        "Das Dokument ist nicht verfügbar.",
    documentDownloadFailed:
        "Das Dokument konnte nicht heruntergeladen werden.",
    userLoadFailed:
        "Die Benutzerdaten konnten nicht geladen werden.",
});

const FILE_NAME = Object.freeze({
    sellerTerms: "Verkaeufer-AGB.pdf",
    sellerPrivacyPolicy:
        "Verkaeufer-Datenschutzerklaerung.pdf",
});

const STYLE = Object.freeze({
    root: {
        spacing: 4,
    },

    headerPaper: {
        display: "block",
        width: "100%",
        overflow: "hidden",
        borderRadius: 3,
    },

    headerBar: {
        minHeight: "148px",
        padding: {
            xs: "24px",
            sm: "30px",
        },
        display: "flex",
        alignItems: "center",
        boxSizing: "border-box",
        color: "white",
    },

    headerContent: {
        width: "100%",
    },

    headerAvatar: {
        width: {
            xs: 72,
            sm: 84,
        },
        height: {
            xs: 72,
            sm: 84,
        },
        backgroundColor:
            "rgba(255,255,255,0.20)",
        border:
            "2px solid rgba(255,255,255,0.30)",
        fontSize: 30,
        fontWeight: 700,
    },

    headerTitle: {
        fontFamily:
            "Arial, Helvetica",
        fontSize: {
            xs: "1.55rem",
            sm: "1.9rem",
        },
        fontWeight: 700,
        lineHeight: 1.2,
    },

    headerSubtitle: {
        marginTop: "4px",
        opacity: 0.86,
        overflowWrap: "anywhere",
    },

    headerChip: {
        color: "white",
        backgroundColor:
            "rgba(255,255,255,0.16)",
        border:
            "1px solid rgba(255,255,255,0.24)",
        fontWeight: 700,
        "& .MuiChip-icon": {
            color: "white",
        },
    },

    sectionTitle: {
        display: "block",
        marginBottom: "12px",
        color: "text.secondary",
        fontWeight: 800,
        letterSpacing: "0.09em",
    },

    overviewPaper: {
        display: "block",
        height: "100%",
        overflow: "hidden",
        borderRadius: 3,
    },

    overviewContent: {
        padding: {
            xs: "18px",
            sm: "24px",
        },
        "&:last-child": {
            paddingBottom: {
                xs: "18px",
                sm: "24px",
            },
        },
    },

    cardHeader: {
        marginBottom: "18px",
    },

    cardIcon: {
        display: "grid",
        placeItems: "center",
        width: 34,
        height: 34,
        borderRadius: 2,
    },

    cardTitle: {
        fontFamily:
            "Arial, Helvetica",
        fontSize: "1rem",
        fontWeight: 700,
    },

    infoLabel: {
        display: "block",
        marginBottom: "3px",
        fontWeight: 600,
    },

    infoValue: {
        overflowWrap: "anywhere",
        lineHeight: 1.45,
    },

    transactionFees: {
        padding: "16px",
        borderRadius: 2.5,
    },

    transactionFeesValue: {
        marginTop: "4px",
        fontSize: "2rem",
        fontWeight: 800,
        lineHeight: 1,
    },

    documentRow: {
        paddingTop: "10px",
        paddingBottom: "10px",
    },

    documentButton: {
        alignSelf: {
            xs: "stretch",
            sm: "center",
        },
        borderRadius: 2,
    },

    imprint: {
        margin: 0,
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
        fontFamily: "inherit",
        lineHeight: 1.65,
    },
});

const createColorStyles = (
    primaryColor,
    disabled,
) => {
    const headerColor = disabled
        ? "#5f6368"
        : primaryColor;

    return Object.freeze({
        headerBar: {
            ...STYLE.headerBar,
            backgroundColor:
                headerColor,
        },

        headerPaper: {
            ...STYLE.headerPaper,
            boxShadow:
                `0 16px 42px ${alpha(
                    headerColor,
                    0.20,
                )}`,
        },

        overviewPaper: {
            ...STYLE.overviewPaper,
            borderColor:
                alpha(
                    primaryColor,
                    0.14,
                ),
            boxShadow:
                `0 12px 30px ${alpha(
                    primaryColor,
                    0.06,
                )}`,
        },

        cardIcon: {
            ...STYLE.cardIcon,
            backgroundColor:
                alpha(
                    primaryColor,
                    0.09,
                ),
            color: primaryColor,
        },

        transactionFees: {
            ...STYLE.transactionFees,
            backgroundColor:
                alpha(
                    primaryColor,
                    0.06,
                ),
        },

        transactionFeesValue: {
            ...STYLE.transactionFeesValue,
            color: primaryColor,
        },
    });
};

const hasValue = (value) => {
    return (
        value !== null
        && value !== undefined
        && value !== ""
    );
};

const displayValue = (value) => {
    if (!hasValue(value)) {
        return MESSAGE.emptyValue;
    }

    return String(value);
};

const formatEnumLabel = (value) => {
    if (!hasValue(value)) {
        return MESSAGE.emptyValue;
    }

    return String(value)
        .toLowerCase()
        .split("_")
        .map((part) => {
            return (
                part.charAt(0).toUpperCase()
                + part.slice(1)
            );
        })
        .join(" ");
};

const formatAddress = (user) => {
    const street = [
        user?.street,
        user?.houseNo,
    ]
        .filter(Boolean)
        .join(" ");

    const town = [
        user?.zipCode,
        user?.town,
    ]
        .filter(Boolean)
        .join(" ");

    const country =
        formatEnumLabel(
            user?.country,
        );

    return [
        street,
        town,
        country,
    ].filter((line) => {
        return (
            hasValue(line)
            && line
            !== MESSAGE.emptyValue
        );
    });
};

const formatImprint = (imprint) => {
    return toEmptyStringIfNull(
        imprint,
    ).replaceAll(
        "$",
        "\n",
    );
};

const formatIban = (
    value,
    visible,
) => {
    if (!hasValue(value)) {
        return MESSAGE.emptyValue;
    }

    const normalized =
        String(value)
            .replace(/\s/g, "")
            .toUpperCase();

    if (visible) {
        return (
            normalized
                .match(/.{1,4}/g)
                ?.join(" ")
            ?? normalized
        );
    }

    if (normalized.length <= 6) {
        return "••••";
    }

    const masked = (
        normalized.slice(0, 2)
        + "•".repeat(
            normalized.length - 6,
        )
        + normalized.slice(-4)
    );

    return (
        masked
            .match(/.{1,4}/g)
            ?.join(" ")
        ?? masked
    );
};

const maskValue = (
    value,
    visible,
    keepEnd = 4,
) => {
    if (!hasValue(value)) {
        return MESSAGE.emptyValue;
    }

    const stringValue =
        String(value);

    if (visible) {
        return stringValue;
    }

    if (
        stringValue.length
        <= keepEnd
    ) {
        return "••••";
    }

    return (
        "•".repeat(
            Math.max(
                4,
                stringValue.length
                - keepEnd,
            ),
        )
        + stringValue.slice(-keepEnd)
    );
};

const getTagLabel = (tag) => {
    if (typeof tag === "string") {
        return tag;
    }

    return (
        tag?.name
        ?? tag?.label
        ?? tag?.tagName
        ?? tag?.value
        ?? displayValue(tag?.id)
    );
};

const getFullName = (user) => {
    return [
        user?.firstName,
        user?.lastName,
    ]
        .filter(Boolean)
        .join(" ");
};

const getAvatarText = (user) => {
    const initials = [
        user?.firstName,
        user?.lastName,
    ]
        .filter(Boolean)
        .map((part) => {
            return part.charAt(0);
        })
        .join("");

    return (
        initials
        || user?.username?.charAt(0)
        || "?"
    ).toUpperCase();
};

const getActiveRoles = (user) => {
    return [
        {
            key: ROLE.admin,
            label: LABEL.administrator,
            active: user?.admin,
        },
        {
            key: ROLE.buyer,
            label: LABEL.buyer,
            active: user?.buyer,
        },
        {
            key: ROLE.seller,
            label: LABEL.seller,
            active: user?.seller,
        },
        {
            key: ROLE.operator,
            label: LABEL.operator,
            active: user?.operator,
        },
    ].filter((role) => {
        return Boolean(role.active);
    });
};

const base64ToBytes = (value) => {
    if (!value) {
        return null;
    }

    if (Array.isArray(value)) {
        return Uint8Array.from(value);
    }

    if (typeof value !== "string") {
        return null;
    }

    let base64 = value.includes(",")
        ? value.slice(
            value.indexOf(",") + 1,
        )
        : value;

    base64 = base64
        .replace(/-/g, "+")
        .replace(/_/g, "/");

    base64 += "=".repeat(
        (
            4
            - base64.length % 4
        ) % 4,
    );

    const binary =
        window.atob(base64);

    const bytes =
        new Uint8Array(
            binary.length,
        );

    for (
        let index = 0;
        index < binary.length;
        index += 1
    ) {
        bytes[index] =
            binary.charCodeAt(index);
    }

    return bytes;
};

const downloadPdf = (
    value,
    fileName,
) => {
    const bytes =
        base64ToBytes(value);

    if (bytes === null) {
        throw new Error(
            MESSAGE.documentUnavailable,
        );
    }

    const blob = new Blob(
        [bytes],
        {
            type: "application/pdf",
        },
    );

    const url =
        window.URL.createObjectURL(
            blob,
        );

    const link =
        document.createElement("a");

    link.href = url;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    link.remove();

    window.setTimeout(
        () => {
            window.URL
                .revokeObjectURL(url);
        },
        0,
    );
};

function InfoRow({
    label,
    value,
    monospace = false,
}) {
    return (
        <Box>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={STYLE.infoLabel}
            >
                {label}
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    ...STYLE.infoValue,
                    fontFamily:
                        monospace
                            ? "monospace"
                            : "inherit",
                }}
            >
                {displayValue(value)}
            </Typography>
        </Box>
    );
}

function SectionTitle({ children }) {
    return (
        <Typography
            variant="overline"
            sx={STYLE.sectionTitle}
        >
            {children}
        </Typography>
    );
}

function OverviewCard({
    title,
    icon,
    action,
    children,
    colorStyle,
}) {
    return (
        <Paper
            variant="outlined"
            sx={
                colorStyle
                    .overviewPaper
            }
        >
            <CardContent
                sx={STYLE.overviewContent}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent=
                    "space-between"
                    spacing={2}
                    sx={STYLE.cardHeader}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.25}
                    >
                        <Box
                            sx={
                                colorStyle
                                    .cardIcon
                            }
                        >
                            {icon}
                        </Box>

                        <Typography
                            variant="h6"
                            sx={STYLE.cardTitle}
                        >
                            {title}
                        </Typography>
                    </Stack>

                    {action ?? null}
                </Stack>

                {children}
            </CardContent>
        </Paper>
    );
}

function DocumentRow({
    title,
    available,
    onDownload,
}) {
    return (
        <Stack
            direction={{
                xs: "column",
                sm: "row",
            }}
            alignItems={{
                xs: "stretch",
                sm: "center",
            }}
            justifyContent=
            "space-between"
            spacing={2}
            sx={STYLE.documentRow}
        >
            <Box>
                <Typography
                    fontWeight={700}
                >
                    {title}
                </Typography>

                <Typography
                    variant="body2"
                    color="text.secondary"
                >
                    {available
                        ? LABEL.pdfDocument
                        : MESSAGE
                            .documentMissing}
                </Typography>
            </Box>

            <Button
                variant="outlined"
                startIcon={
                    <DownloadOutlinedIcon />
                }
                disabled={!available}
                onClick={onDownload}
                sx={STYLE.documentButton}
            >
                {LABEL.download}
            </Button>
        </Stack>
    );
}

function UserOverviewSkeleton() {
    return (
        <Stack spacing={3}>
            <Skeleton
                variant="rounded"
                height={148}
                sx={{
                    borderRadius: 3,
                }}
            />

            <Grid
                container
                spacing={3}
            >
                {[0, 1, 2, 3, 4, 5]
                    .map((item) => {
                        return (
                            <Grid
                                item
                                xs={12}
                                md={6}
                                lg={4}
                                key={item}
                            >
                                <Skeleton
                                    variant="rounded"
                                    height={220}
                                    sx={{
                                        borderRadius: 3,
                                    }}
                                />
                            </Grid>
                        );
                    })}
            </Grid>
        </Stack>
    );
}

function UserHeader({
    user,
    activeRoles,
    colorStyle,
}) {
    const fullName =
        getFullName(user);

    return (
        <Paper sx={colorStyle.headerPaper}>
            <Box sx={colorStyle.headerBar}>
                <Stack
                    direction={{
                        xs: "column",
                        md: "row",
                    }}
                    alignItems={{
                        xs: "flex-start",
                        md: "center",
                    }}
                    spacing={2.5}
                    sx={STYLE.headerContent}
                >
                    <Avatar
                        src={
                            user.portraitUrl
                            || undefined
                        }
                        alt={
                            fullName
                            || user.username
                        }
                        sx={STYLE.headerAvatar}
                    >
                        {getAvatarText(user)}
                    </Avatar>

                    <Box
                        sx={{
                            flexGrow: 1,
                            minWidth: 0,
                        }}
                    >
                        <Typography
                            component="h1"
                            sx={STYLE.headerTitle}
                        >
                            {fullName
                                || user.username}
                        </Typography>

                        <Typography
                            sx={
                                STYLE
                                    .headerSubtitle
                            }
                        >
                            @{displayValue(
                                user.username,
                            )}

                            {user.businessName
                                ? ` · ${user.businessName}`
                                : ""}
                        </Typography>

                        {activeRoles.length > 0
                            && (
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    useFlexGap
                                    flexWrap="wrap"
                                    sx={{
                                        marginTop:
                                            "16px",
                                    }}
                                >
                                    {activeRoles
                                        .map((role) => {
                                            return (
                                                <Chip
                                                    key={
                                                        role.key
                                                    }
                                                    label={
                                                        role.label
                                                    }
                                                    size="small"
                                                    sx={
                                                        STYLE
                                                            .headerChip
                                                    }
                                                />
                                            );
                                        })}
                                </Stack>
                            )}
                    </Box>

                    <Chip
                        icon={
                            user.disabled
                                ? (
                                    <BlockOutlinedIcon />
                                )
                                : undefined
                        }
                        label={
                            user.disabled
                                ? LABEL.disabled
                                : LABEL.active
                        }
                        sx={STYLE.headerChip}
                    />
                </Stack>
            </Box>
        </Paper>
    );
}

function ProfileSection({
    user,
    addressLines,
    tags,
    showTags,
    colorStyle,
}) {
    return (
        <Box>
            <SectionTitle>
                {LABEL.profile}
            </SectionTitle>

            <Grid
                container
                spacing={3}
            >
                <Grid
                    item
                    xs={12}
                    md={6}
                    lg={4}
                >
                    <OverviewCard
                        title={LABEL.person}
                        icon={
                            <PersonOutlineIcon
                                fontSize="small"
                            />
                        }
                        colorStyle={colorStyle}
                    >
                        <Stack spacing={2}>
                            <InfoRow
                                label={LABEL.company}
                                value={
                                    user.businessName
                                }
                            />

                            <InfoRow
                                label={
                                    LABEL.firstName
                                }
                                value={user.firstName}
                            />

                            <InfoRow
                                label={
                                    LABEL.lastName
                                }
                                value={user.lastName}
                            />

                            {user.additionalDirectors != null
                                && user.additionalDirectors.trim() !== "" && (
                                    <InfoRow
                                        label={LABEL.additionalDirectors}
                                        value={user.additionalDirectors}
                                    />
                            )}

                            <InfoRow
                                label={
                                    LABEL.username
                                }
                                value={user.username}
                            />
                        </Stack>
                    </OverviewCard>
                </Grid>

                <Grid
                    item
                    xs={12}
                    md={6}
                    lg={4}
                >
                    <OverviewCard
                        title={LABEL.contact}
                        icon={
                            <ContactMailOutlinedIcon
                                fontSize="small"
                            />
                        }
                        colorStyle={colorStyle}
                    >
                        <Stack spacing={2}>
                            <InfoRow
                                label={
                                    LABEL.emailAddress
                                }
                                value={
                                    user.emailAddress
                                }
                            />

                            <InfoRow
                                label={
                                    LABEL
                                        .mobilePhoneNumber
                                }
                                value={
                                    user
                                        .mobilePhoneNumber
                                }
                            />
                        </Stack>
                    </OverviewCard>
                </Grid>

                <Grid
                    item
                    xs={12}
                    md={6}
                    lg={4}
                >
                    <OverviewCard
                        title={LABEL.address}
                        icon={
                            <HomeOutlinedIcon
                                fontSize="small"
                            />
                        }
                        colorStyle={colorStyle}
                    >
                        {addressLines.length > 0
                            ? (
                                <Stack spacing={0.75}>
                                    {addressLines
                                        .map((line, index) => {
                                            return (
                                                <Typography
                                                    key={
                                                        `${line}-${index}`
                                                    }
                                                    variant="body1"
                                                >
                                                    {line}
                                                </Typography>
                                            );
                                        })}
                                </Stack>
                            )
                            : (
                                <Typography
                                    color=
                                    "text.secondary"
                                >
                                    {
                                        MESSAGE
                                            .noAddress
                                    }
                                </Typography>
                            )}
                    </OverviewCard>
                </Grid>

                {showTags && (
                    <Grid
                        item
                        xs={12}
                    >
                        <OverviewCard
                            title={LABEL.tags}
                            icon={
                                <LabelOutlinedIcon
                                    fontSize="small"
                                />
                            }
                            colorStyle={colorStyle}
                        >
                            {tags.length > 0
                                ? (
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        useFlexGap
                                        flexWrap="wrap"
                                    >
                                        {tags.map((tag, index) => {
                                            const label =
                                                getTagLabel(
                                                    tag,
                                                );

                                            return (
                                                <Chip
                                                    key={
                                                        `${label}-${index}`
                                                    }
                                                    label={label}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        borderRadius: 1.5,
                                                    }}
                                                />
                                            );
                                        })}
                                    </Stack>
                                )
                                : (
                                    <Typography
                                        color=
                                        "text.secondary"
                                    >
                                        {
                                            MESSAGE
                                                .noTags
                                        }
                                    </Typography>
                                )}
                        </OverviewCard>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
}

function BusinessSection({
    user,
    showRegistration,
    showSensitiveData,
    showStripeDetails,
    colorStyle,
    onToggleSensitiveData,
    paymentAction,
}) {
    const sensitiveDataLabel =
        showSensitiveData
            ? LABEL.hideSensitiveData
            : LABEL.showSensitiveData;

    const registrationNumber = [
        user.registrationSection,
        user.registrationNo,
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <Box>
            <SectionTitle>
                {LABEL.businessDetails}
            </SectionTitle>

            <Grid
                container
                spacing={3}
            >
                <Grid
                    item
                    xs={12}
                    lg={6}
                >
                    <OverviewCard
                        title={LABEL.bankAndTax}
                        icon={
                            <AccountBalanceOutlinedIcon
                                fontSize="small"
                            />
                        }
                        colorStyle={colorStyle}
                        action={
                            <Tooltip
                                title={
                                    sensitiveDataLabel
                                }
                            >
                                <IconButton
                                    size="small"
                                    onClick={
                                        onToggleSensitiveData
                                    }
                                    aria-label={
                                        sensitiveDataLabel
                                    }
                                >
                                    {showSensitiveData
                                        ? (
                                            <VisibilityOffOutlinedIcon
                                                fontSize="small"
                                            />
                                        )
                                        : (
                                            <VisibilityOutlinedIcon
                                                fontSize="small"
                                            />
                                        )}
                                </IconButton>
                            </Tooltip>
                        }
                    >
                        <Grid
                            container
                            spacing={2.25}
                        >
                            <Grid
                                item
                                xs={12}
                                sm={6}
                            >
                                <InfoRow
                                    label={LABEL.bank}
                                    value={user.bank}
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                sm={6}
                            >
                                <InfoRow
                                    label={LABEL.bic}
                                    value={maskValue(
                                        user.bic,
                                        showSensitiveData,
                                        3,
                                    )}
                                    monospace
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                            >
                                <InfoRow
                                    label={LABEL.iban}
                                    value={formatIban(
                                        user.iban,
                                        showSensitiveData,
                                    )}
                                    monospace
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                sm={6}
                            >
                                <InfoRow
                                    label={LABEL.taxId}
                                    value={maskValue(
                                        user.taxID,
                                        showSensitiveData,
                                    )}
                                    monospace
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                sm={6}
                            >
                                <InfoRow
                                    label={LABEL.vatId}
                                    value={maskValue(
                                        user.vatID,
                                        showSensitiveData,
                                    )}
                                    monospace
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                sm={6}
                            >
                                <InfoRow
                                    label={LABEL.vat}
                                    value={
                                        user.taxExemption
                                            ? LABEL
                                                .taxExempt
                                            : hasValue(
                                                user.vatRate,
                                            )
                                                ? `${user.vatRate} %`
                                                : MESSAGE
                                                    .emptyValue
                                    }
                                />
                            </Grid>
                        </Grid>
                    </OverviewCard>
                </Grid>

                <Grid
                    item
                    xs={12}
                    md={6}
                    lg={3}
                >
                    <OverviewCard
                        title={
                            LABEL.invoiceDetails
                        }
                        icon={
                            <ReceiptLongOutlinedIcon
                                fontSize="small"
                            />
                        }
                        action={paymentAction}
                        colorStyle={colorStyle}
                    >
                        <Stack spacing={2}>
                            <InfoRow
                                label={
                                    LABEL.invoicePrefix
                                }
                                value={
                                    user.invoicePrefix
                                }
                                monospace
                            />

                            {showStripeDetails
                                && user.stripeId
                                && (
                                    <InfoRow
                                        label={
                                            LABEL.stripeAccountId
                                        }
                                        value={
                                            user.stripeId
                                        }
                                        monospace
                                    />
                                )}

                            {user.seller && (
                                <Box
                                    sx={
                                        colorStyle
                                            .transactionFees
                                    }
                                >
                                    <Typography
                                        variant="caption"
                                        color=
                                        "text.secondary"
                                        fontWeight={700}
                                    >
                                        {
                                            LABEL
                                                .transactionFees
                                        }
                                    </Typography>

                                    <Typography
                                        sx={
                                            colorStyle
                                                .transactionFeesValue
                                        }
                                    >
                                        {hasValue(
                                            user.transactionFees,
                                        )
                                            ? `${user.transactionFees} %`
                                            : MESSAGE
                                                .emptyValue}
                                    </Typography>
                                </Box>
                            )}





                        </Stack>
                    </OverviewCard>
                </Grid>

                {showRegistration && (
                    <Grid
                        item
                        xs={12}
                        md={6}
                        lg={3}
                    >
                        <OverviewCard
                            title={
                                LABEL.registration
                            }
                            icon={
                                <GavelOutlinedIcon
                                    fontSize="small"
                                />
                            }
                            colorStyle={colorStyle}
                        >
                            <Stack spacing={2}>
                                <InfoRow
                                    label={
                                        LABEL
                                            .registrationCourt
                                    }
                                    value={
                                        formatEnumLabel(
                                            user
                                                .registrationCourt,
                                        )
                                    }
                                />

                                <InfoRow
                                    label={
                                        LABEL
                                            .registrationNumber
                                    }
                                    value={
                                        registrationNumber
                                    }
                                    monospace
                                />
                            </Stack>
                        </OverviewCard>
                    </Grid>
                )}

                <Grid
                    item
                    xs={12}
                >
                    <OverviewCard
                        title={LABEL.imprint}
                        icon={
                            <DescriptionOutlinedIcon
                                fontSize="small"
                            />
                        }
                        colorStyle={colorStyle}
                    >
                        <Typography
                            variant="body2"
                            component="pre"
                            sx={STYLE.imprint}
                        >
                            {displayValue(
                                formatImprint(
                                    user.imprint,
                                ),
                            )}
                        </Typography>
                    </OverviewCard>
                </Grid>
            </Grid>
        </Box>
    );
}

function StripeOnboardingDialog({
    open,
    onClose,
    user,
    accessToken,
    showMessage,
}) {
    const [
        canReceive,
        setCanReceive,
    ] = useState(null);

    const [
        ready,
        setReady,
    ] = useState(null);

    const [
        isLoading,
        setIsLoading,
    ] = useState(false);

    const [
        isRedirecting,
        setIsRedirecting,
    ] = useState(false);

    const [
        hasError,
        setHasError,
    ] = useState(false);

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        if (!user?.seller) {
            return undefined;
        }

        let requestIsActive = true;

        setCanReceive(null);
        setReady(null);
        setHasError(false);
        setIsLoading(true);

        handleResponse(
            fetchStripeCanReceiveAPI(
                user.id,
            ),
            (canReceiveResponse) => {
                if (!requestIsActive) {
                    return;
                }

                const canReceivePayments =
                    Boolean(canReceiveResponse);

                setCanReceive(
                    canReceivePayments,
                );

                if (canReceivePayments) {
                    setReady(false);
                    setIsLoading(false);
                    return;
                }

                handleResponse(
                    fetchStripeReadyAPI(
                        user.id,
                        accessToken,
                    ),
                    (readyResponse) => {
                        if (!requestIsActive) {
                            return;
                        }

                        setReady(
                            Boolean(readyResponse),
                        );
                        setIsLoading(false);
                    },
                    (message) => {
                        if (!requestIsActive) {
                            return;
                        }

                        setReady(null);
                        setHasError(true);
                        setIsLoading(false);
                        showMessage(message);
                    },
                );
            },
            (message) => {
                if (!requestIsActive) {
                    return;
                }

                setCanReceive(null);
                setReady(null);
                setHasError(true);
                setIsLoading(false);
                showMessage(message);
            },
        );

        return () => {
            requestIsActive = false;
        };
    }, [
        accessToken,
        open,
        showMessage,
        user?.id,
        user?.seller,
    ]);

    const hasStripeAccount =
        Boolean(user?.stripeId);

    const canStart =
        canReceive === false
        && ready === true;

    const canRefresh =
        canReceive === false
        && ready === false
        && hasStripeAccount;

    const handleRedirect = (
        request,
    ) => {
        setIsRedirecting(true);

        handleResponse(
            request,
            (url) => {
                window.location.assign(
                    String(url),
                );
            },
            (message) => {
                setIsRedirecting(false);
                showMessage(message);
            },
        );
    };

    const handleStart = () => {
        handleRedirect(
            startStripeOnboardingAPI(
                user.id,
                accessToken,
            ),
        );
    };

    const handleRefresh = () => {
        handleRedirect(
            refreshStripeOnboardingAPI(
                user.id,
                accessToken,
            ),
        );
    };

    const handleClose = () => {
        if (isRedirecting) {
            return;
        }

        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>
                Stripe-Zahlungen
            </DialogTitle>

            <DialogContent>
                <Stack
                    spacing={2}
                    sx={{
                        paddingTop: "8px",
                    }}
                >
                    {isLoading && (
                        <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                        >
                            <CircularProgress
                                size={22}
                            />

                            <Typography>
                                Stripe-Status wird geprüft …
                            </Typography>
                        </Stack>
                    )}

                    {!isLoading
                        && canReceive === true
                        && (
                            <Alert severity="success">
                                Das Stripe-Konto ist vollständig
                                eingerichtet und kann Zahlungen
                                empfangen.
                            </Alert>
                        )}

                    {!isLoading
                        && canStart
                        && (
                            <Alert severity="info">
                                Das Stripe-Onboarding kann jetzt
                                gestartet werden.
                            </Alert>
                        )}

                    {!isLoading
                        && canRefresh
                        && (
                            <Alert severity="warning">
                                Für dieses Verkäuferkonto existiert
                                bereits ein Stripe-Konto. Das
                                Onboarding kann fortgesetzt werden.
                            </Alert>
                        )}

                    {!isLoading
                        && canReceive === false
                        && ready === false
                        && !hasStripeAccount
                        && !hasError
                        && (
                            <Alert severity="warning">
                                Das Stripe-Onboarding kann derzeit
                                nicht gestartet werden.
                            </Alert>
                        )}

                    {hasStripeAccount && (
                        <Box>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={STYLE.infoLabel}
                            >
                                {LABEL.stripeAccountId}
                            </Typography>

                            <Typography
                                sx={{
                                    ...STYLE.infoValue,
                                    fontFamily: "monospace",
                                }}
                            >
                                {user.stripeId}
                            </Typography>
                        </Box>
                    )}

                    {hasError && (
                        <Alert severity="error">
                            Der Stripe-Status konnte nicht
                            ermittelt werden.
                        </Alert>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={handleClose}
                    disabled={isRedirecting}
                >
                    Schließen
                </Button>

                {canStart && (
                    <Button
                        variant="contained"
                        onClick={handleStart}
                        disabled={isRedirecting}
                    >
                        {isRedirecting
                            ? "Weiterleitung …"
                            : "Onboarding starten"}
                    </Button>
                )}

                {canRefresh && (
                    <Button
                        variant="contained"
                        onClick={handleRefresh}
                        disabled={isRedirecting}
                    >
                        {isRedirecting
                            ? "Weiterleitung …"
                            : "Onboarding fortsetzen"}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

function SellerDocumentsSection({
    user,
    colorStyle,
    onDownload,
}) {
    return (
        <Box>
            <SectionTitle>
                {LABEL.sellerDocuments}
            </SectionTitle>

            <OverviewCard
                title={LABEL.termsAndPrivacy}
                icon={
                    <SellOutlinedIcon
                        fontSize="small"
                    />
                }
                colorStyle={colorStyle}
            >
                <DocumentRow
                    title={LABEL.sellerTerms}
                    available={Boolean(
                        user.sellerTerms,
                    )}
                    onDownload={() => {
                        onDownload(
                            user.sellerTerms,
                            FILE_NAME.sellerTerms,
                        );
                    }}
                />

                <Divider />

                <DocumentRow
                    title={
                        LABEL
                            .sellerPrivacyPolicy
                    }
                    available={Boolean(
                        user
                            .sellerPrivacyPolicy,
                    )}
                    onDownload={() => {
                        onDownload(
                            user
                                .sellerPrivacyPolicy,
                            FILE_NAME
                                .sellerPrivacyPolicy,
                        );
                    }}
                />
            </OverviewCard>
        </Box>
    );
}

/**
 * Read-only overview of the current user state.
 *
 * Expected Jackson field names for boolean flags:
 * - admin
 * - buyer
 * - seller
 * - operator
 * - disabled
 */
export default function UserOverview({
    appAPI,
    contextAPI,
    userId,
}) {
    const [
        user,
        setUser,
    ] = useState(null);

    const [
        isLoading,
        setIsLoading,
    ] = useState(true);

    const [
        hasLoadError,
        setHasLoadError,
    ] = useState(false);

    const [
        showSensitiveData,
        setShowSensitiveData,
    ] = useState(false);

    const [
        stripeDialogOpen,
        setStripeDialogOpen,
    ] = useState(false);

    const accessToken =
        contextAPI.password;

    const username =
        contextAPI.username;

    const showMessage =
        appAPI.showMessage;

    useEffect(() => {
        let requestIsActive = true;

        setUser(null);
        setIsLoading(true);
        setHasLoadError(false);
        setShowSensitiveData(false);
        setStripeDialogOpen(false);

        handleResponse(
            fetchUserAPI(
                userId,
                accessToken,
            ),
            (response) => {
                if (!requestIsActive) {
                    return;
                }

                setUser(response);
                setIsLoading(false);
            },
            (message) => {
                if (!requestIsActive) {
                    return;
                }

                setUser(null);
                setHasLoadError(true);
                setIsLoading(false);

                showMessage(message);
            },
        );

        return () => {
            requestIsActive = false;
        };
    }, [
        accessToken,
        showMessage,
        userId,
        username,
    ]);

    const addressLines = useMemo(() => {
        return formatAddress(user);
    }, [user]);

    const activeRoles = useMemo(() => {
        return getActiveRoles(user);
    }, [user]);

    const tags = useMemo(() => {
        return Array.isArray(user?.tags)
            ? user.tags
            : [];
    }, [user]);

    if (isLoading) {
        return <UserOverviewSkeleton />;
    }

    if (
        hasLoadError
        || user === null
    ) {
        return (
            <Alert severity="error">
                {MESSAGE.userLoadFailed}
            </Alert>
        );
    }

    const isBusinessUser =
        Boolean(
            user.operator
            || user.seller,
        );

    const showTags =
        Boolean(
            user.buyer
            || user.seller,
        );

    const showRegistration =
        Boolean(
            isBusinessUser
            && user.registered,
        );

    const isOwnUser =
        String(contextAPI.id)
        === String(user.id);

    const canManageStripe =
        Boolean(
            isOwnUser
            && user.seller,
        );

    const colorStyle =
        createColorStyles(
            PRIMARY_COLOR,
            user.disabled,
        );

    const handleDocumentDownload = (
        value,
        fileName,
    ) => {
        try {
            downloadPdf(
                value,
                fileName,
            );
        } catch (error) {
            showMessage(
                error?.message
                || MESSAGE
                    .documentDownloadFailed,
            );
        }
    };

    return (
        <Stack spacing={STYLE.root.spacing}>
            <UserHeader
                user={user}
                activeRoles={activeRoles}
                colorStyle={colorStyle}
            />

            <ProfileSection
                user={user}
                addressLines={addressLines}
                tags={tags}
                showTags={showTags}
                colorStyle={colorStyle}
            />

            {isBusinessUser && (
                <BusinessSection
                    user={user}
                    showRegistration={
                        showRegistration
                    }
                    showSensitiveData={
                        showSensitiveData
                    }
                    showStripeDetails={
                        canManageStripe
                    }
                    colorStyle={colorStyle}
                    onToggleSensitiveData={() => {
                        setShowSensitiveData(
                            (visible) => {
                                return !visible;
                            },
                        );
                    }}
                    paymentAction={
                        canManageStripe
                            ? (
                                <Tooltip
                                    title=
                                    "Stripe-Zahlungen verwalten"
                                >
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            setStripeDialogOpen(
                                                true,
                                            );
                                        }}
                                        aria-label=
                                        "Stripe-Zahlungen verwalten"
                                    >
                                        <PaymentsOutlinedIcon
                                            fontSize="small"
                                        />
                                    </IconButton>
                                </Tooltip>
                            )
                            : null
                    }
                />
            )}

            {user.seller && (
                <SellerDocumentsSection
                    user={user}
                    colorStyle={colorStyle}
                    onDownload={
                        handleDocumentDownload
                    }
                />
            )}

            {canManageStripe && (
                <StripeOnboardingDialog
                    open={stripeDialogOpen}
                    onClose={() => {
                        setStripeDialogOpen(false);
                    }}
                    user={user}
                    accessToken={accessToken}
                    showMessage={showMessage}
                />
            )}
        </Stack>
    );
}